import { useCallback } from 'react';
import useShortcut from 'Hooks/useShortcut';

const defaultGetId = (tab) => tab.id;

// Moves between an ordered list of tabs with mod+ArrowLeft/ArrowRight
// (wraps around). Works for both tab flavors in the app: routing-driven
// tabs (pass getId: (t) => t.url) and redux-driven tabs (default getId,
// items with an `id`). Wrap the container with useShortcutScope and pass
// its id as `scope` when the tabs belong to an overlay (drawer/modal) that
// should take priority over whatever tabs are behind it.
const useTabsShortcuts = ({
  tabs = [], activeId, getId = defaultGetId, onChange, scope = 'global'
} = {}) => {
  const goToOffset = useCallback((offset) => {
    if (!Array.isArray(tabs) || tabs.length === 0 || !onChange) return;

    const currentIndex = tabs.findIndex((tab) => getId(tab) === activeId);
    const baseIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextTab = tabs[(baseIndex + offset + tabs.length) % tabs.length];

    if (nextTab && getId(nextTab) !== activeId) onChange(getId(nextTab));
  }, [tabs, activeId, getId, onChange]);

  const goNext = useCallback(() => goToOffset(1), [goToOffset]);
  const goPrev = useCallback(() => goToOffset(-1), [goToOffset]);

  useShortcut('mod+ArrowRight', goNext, { scope, enabled: Boolean(onChange), description: 'Next tab' });
  useShortcut('mod+ArrowLeft', goPrev, { scope, enabled: Boolean(onChange), description: 'Previous tab' });
};

export default useTabsShortcuts;
